import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Clock, TrendingUp, X } from 'lucide-react';
import { getAllTools } from '../data/tools';
import { getSearchSuggestions } from '../utils/searchUtils';

interface SearchSuggestionsProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSuggestionSelect: (suggestion: string) => void;
  isVisible: boolean;
  onClose: () => void;
}

export const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  searchQuery,
  onSearchChange,
  onSuggestionSelect,
  isVisible,
  onClose
}) => {
  const { t } = useTranslation();
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load search history from localStorage
  useEffect(() => {
    const history = localStorage.getItem('searchHistory');
    if (history) {
      try {
        setSearchHistory(JSON.parse(history));
      } catch (error) {
        console.error('Failed to parse search history:', error);
      }
    }
  }, []);

  // Generate suggestions based on current query
  useEffect(() => {
    if (searchQuery && searchQuery.length >= 2) {
      const allTools = getAllTools();
      const newSuggestions = getSearchSuggestions(allTools, searchQuery, t, 5);
      setSuggestions(newSuggestions);
    } else {
      setSuggestions([]);
    }
  }, [searchQuery, t]);

  // Save search to history
  const saveToHistory = (query: string) => {
    if (!query.trim()) return;
    
    const newHistory = [query, ...searchHistory.filter(item => item !== query)].slice(0, 10);
    setSearchHistory(newHistory);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    onSuggestionSelect(suggestion);
    saveToHistory(suggestion);
    onClose();
  };

  // Handle history item click
  const handleHistoryClick = (historyItem: string) => {
    onSearchChange(historyItem);
    onSuggestionSelect(historyItem);
    onClose();
  };

  // Clear search history
  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('searchHistory');
  };

  // Popular search terms (could be dynamic based on analytics)
  const popularSearches = [
    'JSON',
    'Base64',
    'URL encoder',
    'QR code',
    'Color picker',
    'Password generator',
    'Hash generator',
    'Text diff'
  ];

  if (!isVisible) return null;

  return (
    <div 
      ref={containerRef}
      className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
    >
      {/* Suggestions based on current query */}
      {suggestions.length > 0 && (
        <div className="p-2">
          <div className="flex items-center text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
            <Search className="w-3 h-3 mr-1" />
            {t('search.suggestions')}
          </div>
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              <span className="text-gray-900 dark:text-gray-100">{suggestion}</span>
            </button>
          ))}
        </div>
      )}

      {/* Search History */}
      {searchHistory.length > 0 && suggestions.length === 0 && (
        <div className="p-2">
          <div className="flex items-center justify-between text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
            <div className="flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              {t('search.recentSearches')}
            </div>
            <button
              onClick={clearHistory}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          {searchHistory.slice(0, 5).map((historyItem, index) => (
            <button
              key={index}
              onClick={() => handleHistoryClick(historyItem)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors flex items-center"
            >
              <Clock className="w-3 h-3 mr-2 text-gray-400" />
              <span className="text-gray-900 dark:text-gray-100">{historyItem}</span>
            </button>
          ))}
        </div>
      )}

      {/* Popular Searches */}
      {suggestions.length === 0 && searchHistory.length === 0 && (
        <div className="p-2">
          <div className="flex items-center text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
            <TrendingUp className="w-3 h-3 mr-1" />
            {t('search.popularSearches')}
          </div>
          {popularSearches.map((popular, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(popular)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors flex items-center"
            >
              <TrendingUp className="w-3 h-3 mr-2 text-gray-400" />
              <span className="text-gray-900 dark:text-gray-100">{popular}</span>
            </button>
          ))}
        </div>
      )}

      {/* No suggestions message */}
      {suggestions.length === 0 && searchHistory.length === 0 && searchQuery.length >= 2 && (
        <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
          {t('search.noSuggestions')}
        </div>
      )}
    </div>
  );
};
